import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Target,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Globe,
  Briefcase,
  Users,
  ChevronLeft,
  ChevronRight,
  Search,
  Upload,
  X,
  ClipboardList,
  RefreshCcw,
} from 'lucide-react'
import { bookingApi, sessionsApi } from '../../lib/api/sessions'
import { profileApi } from '../../lib/api/profile'
import { adminApi } from '../../lib/api/admin'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import { sanitizeHtml } from '../../lib/utils/sanitizeHtml'
import { useProfileCompletion } from '../../contexts/profileCompletion'

// Must stay in sync with JOB_DESCRIPTION_MAX_LENGTH in the backend
// (app/shared/schemas/limits.py) — real postings run ~7,000 characters.
const JOB_DESC_MAX_LENGTH = 10000

interface CalendlySlot {
  start: string
  start_utc: string
  end: string
  is_booked: boolean
}

interface CalendlyInterviewer {
  interviewer_id: number
  alias: string
  avatar_url: string | null
  language: string | null
  interviewer_timezone: string | null
  slots: CalendlySlot[]
}

interface InterviewerProfile {
  id: number
  alias: string
  avatar_url: string | null
  specializations: string | null
  tier: string | null
  hourly_rate: number
  language: string | null
  is_available: boolean
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export default function ClientScheduleSession() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Reschedule mode
  const rescheduleId = searchParams.get('rescheduleId')
  const isReschedule = !!rescheduleId
  const [originalBookingId, setOriginalBookingId] = useState<number | null>(null)
  const [originalInterviewerId, setOriginalInterviewerId] = useState<number | null>(null)

  const GOAL_OPTIONS = [
    'Build Confidence',
    'Improve Interview Answers (Behavioral & Situational)',
    'Tell My Story More Effectively (Tell Me About Yourself / Resume)',
    'Prepare for a Specific Role or Company',
    'Strengthen Communication & Presence',
    'Get Honest, Detailed Feedback',
  ]

  // Profile form
  const [selectedGoals, setSelectedGoals] = useState<string[]>([])
  const [goalsOther, setGoalsOther] = useState('')
  const [goals, setGoals] = useState('')
  const [jobDesc, setJobDesc] = useState('')
  const [targetRole, setTargetRole] = useState('')
  const [hasInterview, setHasInterview] = useState('')
  const [timezone, setTimezone] = useState('')
  const [language, setLanguage] = useState('')
  const [specialNotes, setSpecialNotes] = useState('')
  const [profileName, setProfileName] = useState('')
  const [profileEmail, setProfileEmail] = useState('')
  const [initialLoading, setInitialLoading] = useState(true)

  // Validation modal
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [showValidation, setShowValidation] = useState(false)

  // Calendar
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth())
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear())
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set())
  const slotsSectionRef = useRef<HTMLDivElement | null>(null)

  // Profile-incomplete interstitial — shown once per browser session when the
  // client lands here without a complete profile.
  const { isComplete: profileIsComplete } = useProfileCompletion()
  const [showProfileInterstitial, setShowProfileInterstitial] = useState(false)
  useEffect(() => {
    if (profileIsComplete) return
    if (sessionStorage.getItem('idr-schedule-interstitial-dismissed') === '1') return
    setShowProfileInterstitial(true)
  }, [profileIsComplete])

  // Interviewers
  const [interviewerProfiles, setInterviewerProfiles] = useState<InterviewerProfile[]>([])
  const [availabilityData, setAvailabilityData] = useState<CalendlyInterviewer[]>([])
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsFetched, setSlotsFetched] = useState(false)

  // Selection
  const [selectedInterviewerId, setSelectedInterviewerId] = useState<number | null>(null)
  const [selectedSlotUtc, setSelectedSlotUtc] = useState<string | null>(null)
  const [selectedSlotStart, setSelectedSlotStart] = useState<string | null>(null)

  // Job Description PDF
  const [jobDescFile, setJobDescFile] = useState<File | null>(null)
  const [jobDescPath, setJobDescPath] = useState<string | null>(null)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [pdfError, setPdfError] = useState('')

  // Session duration from platform settings
  const [sessionDuration, setSessionDuration] = useState<number>(60)

  // Session price from backend config
  const [sessionPrice, setSessionPrice] = useState<number | null>(null)

  // Booking
  const [bookingLoading, setBookingLoading] = useState(false)
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [tcAccepted, setTcAccepted] = useState(false)

  // Terms & Privacy modals — content is admin-editable (admin_terms_policies table)
  type PolicyItem = { policy_key: string; title: string; body_html: string }
  const [allPolicies, setAllPolicies] = useState<PolicyItem[]>([])
  const [policiesLoading, setPoliciesLoading] = useState(false)
  const [policiesError, setPoliciesError] = useState<string | null>(null)
  const [showTcModal, setShowTcModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const ensurePoliciesLoaded = async () => {
    if (allPolicies.length > 0 || policiesLoading) return
    setPoliciesLoading(true)
    setPoliciesError(null)
    try {
      const data = await adminApi.getPublicTermsPolicies()
      setAllPolicies(Array.isArray(data) ? data : [])
    } catch {
      setPoliciesError('Unable to load the policy content right now. Please try again.')
    } finally {
      setPoliciesLoading(false)
    }
  }

  const openTcModal = async () => {
    setShowTcModal(true)
    await ensurePoliciesLoaded()
  }

  const openPaymentModal = async () => {
    setShowPaymentModal(true)
    await ensurePoliciesLoaded()
  }

  const tcPolicy = allPolicies.find(p => p.policy_key === 'terms_conditions') || null
  const paymentPolicy = allPolicies.find(p => p.policy_key === 'payment_policy') || null

  // Load profile + interviewer list on mount; pre-fill from booking if reschedule mode
  useEffect(() => {
    setInitialLoading(true)
    // Reset form state so stale values don't leak if URL rescheduleId changes
    setGoals(''); setSelectedGoals([]); setGoalsOther(''); setTargetRole(''); setJobDesc(''); setJobDescPath(null)
    setSpecialNotes(''); setHasInterview(''); setSelectedInterviewerId(null)
    setSelectedSlotUtc(null); setSelectedSlotStart(null); setOriginalBookingId(null); setOriginalInterviewerId(null)

    adminApi.getPublicSettings().then((s) => {
      const d = parseInt(s?.defaultSessionDuration ?? '60', 10)
      if (!isNaN(d) && d > 0) setSessionDuration(d)
    }).catch(() => {})

    bookingApi.getSessionPrice().then((r) => setSessionPrice(r.session_price_usd)).catch(() => {})

    const fetches: Promise<any>[] = [
      profileApi.getProfile().catch(() => null),
      bookingApi.getInterviewers({ per_page: 50 }).catch(() => []),
    ]
    if (rescheduleId) {
      fetches.push(sessionsApi.getBooking(Number(rescheduleId)).catch(() => null))
    }
    Promise.all(fetches).then(([profileResp, ivs, booking]) => {
      // axios interceptor auto-unwraps DataResponse envelope
      if (profileResp) {
        setGoals(profileResp.interview_goals || '')
        setProfileName(profileResp.full_name || '')
        setProfileEmail(profileResp.email || '')
        setTimezone(profileResp.timezone || '')
        setLanguage(profileResp.language || '')
      }
      const list = Array.isArray(ivs) ? ivs : (ivs?.data || [])
      setInterviewerProfiles(list.filter((iv: InterviewerProfile) => iv.is_available))

      // Pre-fill from booking (reschedule mode) — booking values override profile defaults
      if (booking) {
        setOriginalBookingId(booking.booking_id)
        if (booking.job_description_text) setJobDesc(booking.job_description_text)
        if (booking.job_description_path) setJobDescPath(booking.job_description_path)
        if (booking.special_notes) setSpecialNotes(booking.special_notes)
        if (booking.target_role) setTargetRole(booking.target_role)
        if (booking.interview_goals) {
          const parts = booking.interview_goals.split('; ').map((p: string) => p.trim()).filter(Boolean)
          setSelectedGoals(parts.filter((p: string) => GOAL_OPTIONS.includes(p)))
          setGoalsOther(parts.filter((p: string) => !GOAL_OPTIONS.includes(p)).join('; '))
          setGoals(booking.interview_goals)
        }
        // Restore the client's original answer. Bookings made before this was
        // persisted have `null` — leave the radio unanswered so the client
        // re-picks, rather than asserting an answer they may not have given.
        if (booking.has_real_interview === true) setHasInterview('yes')
        else if (booking.has_real_interview === false) setHasInterview('no')
        if (booking.interviewer_id) {
          setSelectedInterviewerId(booking.interviewer_id)
          setOriginalInterviewerId(booking.interviewer_id)
        }
      }
    }).finally(() => setInitialLoading(false))
  }, [rescheduleId])

  // Release slot when user cancels payment on Stripe checkout page
  useEffect(() => {
    const paymentStatus = searchParams.get('payment')
    const sessionId = searchParams.get('session_id')
    if (paymentStatus === 'cancelled' && sessionId) {
      sessionStorage.removeItem('pendingCheckoutSession')
      bookingApi.cancelBookingPayment(sessionId).catch(() => {})
    }
  }, [])

  // On mount: cancel any pending checkout session left over from a previous visit
  // (e.g. user hit browser back from Stripe as a full page reload, not bfcache restore).
  useEffect(() => {
    const pendingSession = sessionStorage.getItem('pendingCheckoutSession')
    if (pendingSession) {
      sessionStorage.removeItem('pendingCheckoutSession')
      bookingApi.cancelBookingPayment(pendingSession).catch(() => {})
      toast('Your previous slot reservation was released. Please select a slot to continue.')
    }
  }, [])

  // Handle browser back button (bfcache restore): unfreeze button + release slot + clear stale slots
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setBookingLoading(false)
        const pendingSession = sessionStorage.getItem('pendingCheckoutSession')
        if (pendingSession) {
          sessionStorage.removeItem('pendingCheckoutSession')
          bookingApi.cancelBookingPayment(pendingSession).catch(() => {})
          setAvailabilityData([])
          setSlotsFetched(false)
          setSelectedInterviewerId(null)
          setSelectedSlotUtc(null)
          setSelectedSlotStart(null)
          toast('Your previous slot reservation was released. Please select a slot to continue.')
        }
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  // Fetch month-level availability so the calendar can bold dates with slots.
  useEffect(() => {
    let cancelled = false
    bookingApi
      .getCalendlyMonthAvailability(calendarYear, calendarMonth + 1)
      .then((res) => {
        if (cancelled) return
        setDatesWithSlots(new Set(res.dates_with_slots || []))
      })
      .catch(() => {
        if (!cancelled) setDatesWithSlots(new Set())
      })
    return () => { cancelled = true }
  }, [calendarYear, calendarMonth])

  // ── Validation ────────────────────────────────────────────────────────────────

  const buildGoalsString = (): string => {
    const parts = [...selectedGoals]
    if (goalsOther.trim()) parts.push(goalsOther.trim())
    return parts.join('; ')
  }

  const getMissingFields = (): string[] => {
    const missing: string[] = []
    if (!hasInterview) missing.push('Do you have a REAL interview scheduled that you are prepping for?')
    if (!targetRole.trim()) missing.push('Target Role')
    if (selectedGoals.length === 0 && !goalsOther.trim()) missing.push('Interview Goals')
    if (hasInterview === 'yes') {
      if (!jobDesc.trim() && !jobDescPath) missing.push('Job Description')
    }
    if (!timezone) missing.push('Timezone (in Preferences)')
    if (!language) missing.push('Language (in Preferences)')
    return missing
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const toDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  const fmtTime = (iso: string) => {
    const match = iso.match(/T(\d{2}):(\d{2})/)
    if (!match) return iso
    const h = parseInt(match[1])
    const m = parseInt(match[2])
    const period = h >= 12 ? 'PM' : 'AM'
    const h12 = h % 12 || 12
    return `${h12}:${m.toString().padStart(2, '0')} ${period}`
  }

  const getTzAbbr = (tz: string): string => {
    if (!tz) return ''
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short',
      }).formatToParts(new Date())
      return parts.find((p) => p.type === 'timeZoneName')?.value || tz
    } catch {
      return tz
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setAvailabilityData([])
    setSlotsFetched(false)
    setSelectedInterviewerId(null)
    setSelectedSlotUtc(null)
    setSelectedSlotStart(null)
    setErrorMsg('')
  }

  const handleGetSlots = () => {
    if (!selectedDate) return

    const missing = getMissingFields()
    if (missing.length > 0) {
      setValidationErrors(missing)
      setShowValidation(true)
      return
    }

    setSlotsLoading(true)
    setAvailabilityData([])
    setSlotsFetched(false)
    setSelectedInterviewerId(null)
    setSelectedSlotUtc(null)
    setSelectedSlotStart(null)
    setErrorMsg('')

    bookingApi.getCalendlyAvailability(toDateStr(selectedDate), timezone, language)
      .then((result) => {
        setAvailabilityData(result?.interviewers || [])
        setSlotsFetched(true)
        // Scroll the slot results into view so the user sees the new content.
        requestAnimationFrame(() => {
          slotsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
      .catch(() => {
        setErrorMsg('Failed to load slots. Please try again.')
        setSlotsFetched(true)
      })
      .finally(() => setSlotsLoading(false))
  }

  const handleSlotSelect = (interviewerId: number, slotUtc: string, slotStart: string) => {
    setSelectedInterviewerId(interviewerId)
    setSelectedSlotUtc(slotUtc)
    setSelectedSlotStart(slotStart)
    setTcAccepted(false)
    setErrorMsg('')
  }

  const handlePdfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      setPdfError('Only PDF files are allowed.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setPdfError('File size exceeds the 5 MB limit.')
      return
    }
    setPdfError('')
    setJobDescFile(file)
    setJobDescPath(null)
    setUploadingPdf(true)
    try {
      const result = await bookingApi.uploadJobDescriptionPdf(file)
      setJobDescPath(result.path)

    } catch {
      setPdfError('Upload failed. Please try again.')
      setJobDescFile(null)
    } finally {
      setUploadingPdf(false)
    }
  }

  const handlePdfRemove = () => {
    setJobDescFile(null)
    setJobDescPath(null)
    setPdfError('')
  }

  const handleBook = async () => {
    if (!selectedInterviewerId || !selectedSlotUtc) return

    const missing = getMissingFields()
    if (missing.length > 0) {
      setValidationErrors(missing)
      setShowValidation(true)
      return
    }

    setBookingLoading(true)
    setErrorMsg('')
    try {
      const result = await bookingApi.initiateBookingPayment({
        interviewer_id: selectedInterviewerId,
        start_utc: selectedSlotUtc,
        invitee_name: profileName || 'Guest',
        invitee_email: profileEmail || 'guest@example.com',
        job_description_path: jobDescPath || undefined,
        job_description_text: jobDesc || undefined,
        special_notes: specialNotes || undefined,
        target_role: targetRole || undefined,
        interview_goals: buildGoalsString() || undefined,
        has_real_interview: hasInterview === 'yes',
        client_timezone: timezone || undefined,
      })
      setCheckoutUrl(result.checkout_url)
      // Record the reserved checkout session so that if the user navigates back
      // (browser Back / bfcache) instead of using Stripe's cancel button, the
      // mount/pageshow handlers can release the held slot immediately.
      if (result.checkout_session_id) {
        sessionStorage.setItem('pendingCheckoutSession', result.checkout_session_id)
      }
      window.location.href = result.checkout_url
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Booking failed. Please try again.'
      toast.error(msg)
      setBookingLoading(false)
    }
  }

  const handleReschedule = async () => {
    if (!selectedInterviewerId || !selectedSlotUtc || !originalBookingId) return

    setBookingLoading(true)
    setErrorMsg('')
    try {
      await sessionsApi.rescheduleBookingFull(originalBookingId, {
        new_slot_start_utc: selectedSlotUtc,
        new_interviewer_id: selectedInterviewerId,
        job_description_text: jobDesc || undefined,
        job_description_path: jobDescPath || undefined,
        special_notes: specialNotes || undefined,
        target_role: targetRole || undefined,
        interview_goals: buildGoalsString() || goals || undefined,
        has_real_interview: hasInterview === 'yes',
      })
      navigate('/client/interviews')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Reschedule failed. Please try again.'
      toast.error(msg)
      setBookingLoading(false)
    }
  }

  // ── Calendar grid ─────────────────────────────────────────────────────────────

  const getCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth, 1)
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0)
    const startPad = (firstDay.getDay() + 6) % 7
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const days: { day: number | null; date?: Date; disabled: boolean; empty: boolean; today?: boolean; selected?: boolean }[] = []

    for (let i = 0; i < startPad; i++) days.push({ day: null, disabled: true, empty: true })
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(calendarYear, calendarMonth, d); date.setHours(0, 0, 0, 0)
      days.push({
        day: d,
        date,
        disabled: date < today,
        empty: false,
        today: date.getTime() === today.getTime(),
        selected: selectedDate?.getTime() === date.getTime(),
      })
    }
    return days
  }

  if (initialLoading) return <LoadingSpinner message="Loading..." />

  // Build slot lookup from availability data
  const slotsById: Record<number, CalendlySlot[]> = {}
  for (const iv of availabilityData) {
    slotsById[iv.interviewer_id] = iv.slots.filter(s => !s.is_booked)
  }
  const totalOpenSlots = Object.values(slotsById).reduce((n, s) => n + s.length, 0)

  // Match quality per interviewer
  type MatchQuality = 'full' | 'partial' | 'none'
  const matchById: Record<number, MatchQuality> = {}
  const tzById: Record<number, string | null> = {}
  const langById: Record<number, string | null> = {}
  if (slotsFetched && timezone && language) {
    for (const iv of availabilityData) {
      const tzMatch = iv.interviewer_timezone === timezone
      const langMatch = iv.language === language
      matchById[iv.interviewer_id] =
        tzMatch && langMatch   ? 'full'
        : tzMatch || langMatch ? 'partial'
        : 'none'
      tzById[iv.interviewer_id] = iv.interviewer_timezone ?? null
      langById[iv.interviewer_id] = iv.language ?? null
    }
  }

  const availIds = new Set(availabilityData.map(iv => iv.interviewer_id))
  const orderedProfiles: InterviewerProfile[] = (() => {
    const withSlots = availabilityData
      .map(iv => interviewerProfiles.find(p => p.id === iv.interviewer_id))
      .filter((p): p is InterviewerProfile => p !== undefined)
    // After slots are fetched, only include interviewers that have slots.
    // In reschedule mode, also keep the original interviewer so the user can
    // see who they had before (they may still appear with slots, but if not,
    // this preserves context without showing random empty interviewers).
    const withoutSlots = slotsFetched
      ? (isReschedule && originalInterviewerId
          ? interviewerProfiles.filter(p => !availIds.has(p.id) && p.id === originalInterviewerId)
          : [])
      : interviewerProfiles.filter(p => !availIds.has(p.id))
    const all = [...withSlots, ...withoutSlots]
    // In reschedule mode: always show current interviewer first
    if (originalInterviewerId) {
      const idx = all.findIndex(p => p.id === originalInterviewerId)
      if (idx > 0) { const [cur] = all.splice(idx, 1); all.unshift(cur) }
    }
    return all
  })()

  return (
    <div className="client-schedule">
      <div className="page-header">
        <h1 className="page-title">{isReschedule ? 'Reschedule Session' : 'Schedule a Session'}</h1>
        <p className="page-subtitle">
          {isReschedule
            ? 'Your previous details are pre-filled. Select a new date, slot and interviewer, then confirm.'
            : 'Fill in your details, pick a date, then get available slots'}
        </p>
      </div>

      {isReschedule && (
        <div className="sch-alert" style={{ background: '#fffbeb', borderColor: '#f59e0b', color: '#92400e' }}>
          <RefreshCcw size={18} />
          <span>Reschedule mode — no payment required. Old interviewer will receive a cancellation email if you change interviewer.</span>
        </div>
      )}

      {isReschedule && originalInterviewerId && (() => {
        const currentIv = interviewerProfiles.find(p => p.id === originalInterviewerId)
        if (!currentIv) return null
        const specs = currentIv.specializations
          ? currentIv.specializations.split(',').filter(Boolean).slice(0, 3)
          : []
        return (
          <div className="current-iv-banner">
            <div className="current-iv-label">
              <Users size={15} />
              <span>Current Interviewer</span>
            </div>
            <div className="current-iv-body">
              <div className="iv-avatar" style={{ width: 40, height: 40, fontSize: 16, flexShrink: 0 }}>
                {currentIv.alias.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div className="current-iv-name">{currentIv.alias}</div>
                <div className="iv-tags" style={{ marginTop: 4 }}>
                  {currentIv.tier && <span className="tag">{currentIv.tier}</span>}
                  {currentIv.language && <span className="tag">{currentIv.language.toUpperCase()}</span>}
                  {specs.map(s => <span key={s} className="tag">{s.trim()}</span>)}
                </div>
              </div>
              <span className="current-iv-hint">You can keep this interviewer or select a different one below.</span>
            </div>
          </div>
        )
      })()}

      {/* Payment cancelled banner */}
      {searchParams.get('payment') === 'cancelled' && (
        <div className="sch-alert error">
          <AlertCircle size={18} />
          <span>Payment was cancelled. Your slot was released — you can book again below.</span>
        </div>
      )}

      {errorMsg && (
        <div className="sch-alert error">
          <AlertCircle size={18} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* ── Profile-incomplete interstitial ────────────────────────────── */}
      {showProfileInterstitial && (
        <div
          className="val-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowProfileInterstitial(false)
          }}
        >
          <div className="val-modal" onClick={e => e.stopPropagation()}>
            <div className="val-modal-header">
              <AlertCircle size={22} />
              <h3>Set up your profile first</h3>
              <button
                className="val-close"
                onClick={() => {
                  sessionStorage.setItem('idr-schedule-interstitial-dismissed', '1')
                  setShowProfileInterstitial(false)
                }}
              >
                <X size={18} />
              </button>
            </div>
            <p className="val-modal-desc">
              Before we schedule your session, let's set up your profile so you get the most value from your experience. Your interviewer uses these details to tailor questions to your role.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  sessionStorage.setItem('idr-schedule-interstitial-dismissed', '1')
                  setShowProfileInterstitial(false)
                  navigate('/client/profile')
                }}
              >
                Complete My Profile
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  sessionStorage.setItem('idr-schedule-interstitial-dismissed', '1')
                  setShowProfileInterstitial(false)
                }}
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Validation Modal ───────────────────────────────────────────── */}
      {showValidation && (
        <div className="val-overlay" onClick={() => setShowValidation(false)}>
          <div className="val-modal" onClick={e => e.stopPropagation()}>
            <div className="val-modal-header">
              <AlertCircle size={22} />
              <h3>Please complete required fields</h3>
              <button className="val-close" onClick={() => setShowValidation(false)}>
                <X size={18} />
              </button>
            </div>
            <p className="val-modal-desc">
              The following fields are required before you can proceed:
            </p>
            <ul className="val-list">
              {validationErrors.map(err => (
                <li key={err}>
                  <AlertCircle size={14} />
                  {err}
                </li>
              ))}
            </ul>
            <button className="btn btn-primary val-ok-btn" onClick={() => setShowValidation(false)}>
              OK, I'll fill them in
            </button>
          </div>
        </div>
      )}

      <div className="sch-grid">

        {/* ── Interview question ─────────────────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header">
            <Target size={20} />
            <h3>Do you have a REAL interview scheduled that you are prepping for? *</h3>
          </div>
          <div className="radio-group">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className={`radio-option${hasInterview === v ? ' selected' : ''}`}>
                <input type="radio" name="has_real_interview" value={v}
                  checked={hasInterview === v} onChange={() => setHasInterview(v)} />
                <div>
                  <strong>{v === 'yes' ? 'Yes' : 'No'}</strong>
                  <span>{v === 'yes' ? 'I have an upcoming interview and want to practice' : 'I want general interview practice for my target role'}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* ── Target role ────────────────────────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header"><Briefcase size={20} /><h3>Target Role *</h3></div>
          <input type="text" value={targetRole} onChange={e => setTargetRole(e.target.value.slice(0, 50))}
            placeholder="e.g., Senior Software Engineer, Product Manager" className="form-input" maxLength={50} />
          <div style={{ textAlign: 'right', fontSize: 11, color: targetRole.length >= 45 ? '#ef4444' : 'var(--text-muted)', marginTop: 4 }}>
            {targetRole.length}/50
          </div>
        </div>

        {/* ── Goals + Job description ────────────────────────────────── */}
        <div className="card">
          <div className="sch-card-header"><Target size={20} /><h3>Interview Goals * <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>— select all that apply</span></h3></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {GOAL_OPTIONS.map((opt) => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: 'var(--text-dark)' }}>
                <input
                  type="checkbox"
                  checked={selectedGoals.includes(opt)}
                  onChange={() => setSelectedGoals((prev) =>
                    prev.includes(opt) ? prev.filter((g) => g !== opt) : [...prev, opt]
                  )}
                  style={{ width: 18, height: 18, accentColor: 'var(--primary-navy)', flexShrink: 0 }}
                />
                {opt}
              </label>
            ))}
          </div>
          <textarea value={goalsOther} onChange={(e) => setGoalsOther(e.target.value.slice(0, 200))}
            placeholder="Anything else? (optional)"
            rows={2} className="form-input" style={{ resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }} maxLength={200} />
          <div style={{ textAlign: 'right', fontSize: 11, color: goalsOther.length >= 180 ? '#ef4444' : 'var(--text-muted)', marginTop: 4 }}>
            {goalsOther.length}/200
          </div>
        </div>

        <div className="card">
          <div className="sch-card-header">
            <FileText size={20} />
            <h3>Job Description{hasInterview === 'yes' ? ' *' : ''}</h3>
          </div>
          {hasInterview === 'yes' && (
            <div className="required-notice">
              <AlertCircle size={15} />
              <span>
                Since you have a real interview scheduled, please share the job description so your interviewer can tailor the session.
              </span>
            </div>
          )}

          {/* PDF Upload */}
          <div className="jd-upload-row">
            {!jobDescFile && !jobDescPath ? (
              <label className="jd-upload-btn">
                <Upload size={15} />
                <span>Upload Job Description (PDF)</span>
                <input type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
                  onChange={handlePdfSelect} />
              </label>
            ) : jobDescPath && !jobDescFile ? (
              /* Previously uploaded PDF (reschedule pre-fill) */
              <div className="jd-file-pill uploaded">
                <CheckCircle size={14} />
                <span>{jobDescPath.split('/').pop()}</span>
                <button type="button" className="jd-remove-btn" onClick={handlePdfRemove}
                  title="Remove file"><X size={13} /></button>
              </div>
            ) : (
              <div className={`jd-file-pill${uploadingPdf ? ' uploading' : jobDescPath ? ' uploaded' : ''}`}>
                {uploadingPdf
                  ? <><div className="spin-sm" /><span>Uploading...</span></>
                  : jobDescPath
                    ? <><CheckCircle size={14} /><span>{jobDescFile!.name}</span></>
                    : <><span>{jobDescFile!.name}</span></>
                }
                {!uploadingPdf && (
                  <button type="button" className="jd-remove-btn" onClick={handlePdfRemove}
                    title="Remove file"><X size={13} /></button>
                )}
              </div>
            )}
            {pdfError && <span className="jd-error">{pdfError}</span>}
          </div>

          <div className="jd-divider"><span>or paste text below</span></div>

          <textarea value={jobDesc} onChange={e => setJobDesc(e.target.value.slice(0, JOB_DESC_MAX_LENGTH))}
            placeholder="Paste the full job description here..." rows={12} className="form-input"
            style={{ resize: 'vertical', minHeight: 220, fontFamily: 'inherit' }} maxLength={JOB_DESC_MAX_LENGTH} />
          <div style={{ textAlign: 'right', fontSize: 11, color: jobDesc.length >= JOB_DESC_MAX_LENGTH * 0.9 ? '#ef4444' : 'var(--text-muted)', marginTop: 4 }}>
            {jobDesc.length.toLocaleString()}/{JOB_DESC_MAX_LENGTH.toLocaleString()}
          </div>

        </div>

        {/* ── Special Notes or Circumstances ─────────────────────────── */}
        <div className="card full-width">
          <div className="sch-card-header">
            <ClipboardList size={20} />
            <h3>Special Notes or Circumstances</h3>
          </div>
          <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>
            Any special requirements or scheduling preferences? (Optional)
          </label>
          <textarea
            value={specialNotes}
            onChange={e => setSpecialNotes(e.target.value.slice(0, 300))}
            placeholder="e.g., I prefer a female interviewer, I need accessibility accommodations, I have a specific company in mind..."
            rows={4}
            className="form-input"
            style={{ resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
            maxLength={300}
          />
          <div style={{ textAlign: 'right', fontSize: 11, color: specialNotes.length >= 270 ? '#ef4444' : 'var(--text-muted)', marginTop: 4 }}>
            {specialNotes.length}/300
          </div>
        </div>

        {/* ── Preferences + Calendar ─────────────────────────────────── */}
        <div className="full-width prefs-cal-row">

          {/* Preferences */}
          <div className="card">
            <div className="sch-card-header"><Globe size={20} /><h3>Preferences *</h3></div>
            <div className="form-group">
              <label className="form-label">Duration</label>
              <div className="duration-fixed"><Clock size={15} /><span>{sessionDuration} minutes</span></div>
            </div>
            <div className="form-group">
              <label className="form-label">Timezone *</label>
              <select value={timezone} onChange={e => { setTimezone(e.target.value); setErrorMsg(''); setAvailabilityData([]); setSlotsFetched(false) }} className="form-input"
                style={selectedDate && !timezone ? { borderColor: '#dc2626' } : undefined}>
                <option value="">Select your timezone</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">GMT (London)</option>
                <option value="Europe/Paris">Central European (CET)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Tokyo">Japan (JST)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Language *</label>
              <select value={language} onChange={e => { setLanguage(e.target.value); setErrorMsg(''); setAvailabilityData([]); setSlotsFetched(false) }} className="form-input"
                style={selectedDate && !language ? { borderColor: '#dc2626' } : undefined}>
                <option value="">Select language</option>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese (Mandarin)</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
                <option value="pt">Portuguese</option>
                <option value="hi">Hindi</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
          </div>

          {/* Calendar */}
          <div className="card">
            <div className="sch-card-header"><Calendar size={20} /><h3>Select Date *</h3></div>
            <div className="cal-header">
              <button type="button" className="cal-nav" onClick={() => {
                if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1) }
                else setCalendarMonth(m => m - 1)
              }}><ChevronLeft size={15} /></button>
              <span className="cal-month">{monthNames[calendarMonth]} {calendarYear}</span>
              <button type="button" className="cal-nav" onClick={() => {
                if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1) }
                else setCalendarMonth(m => m + 1)
              }}><ChevronRight size={15} /></button>
            </div>
            <div className="cal-grid">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="cal-day-hdr">{d}</div>
              ))}
              {getCalendarDays().map((day, i) => {
                const dateKey = day.date ? toDateStr(day.date) : ''
                const hasSlots = !!dateKey && datesWithSlots.has(dateKey)
                return (
                  <button key={i} type="button" disabled={day.disabled || day.empty}
                    onClick={() => { if (!day.disabled && !day.empty && day.date) handleDateSelect(day.date) }}
                    className={`cal-day${day.empty ? ' empty' : ''}${day.disabled && !day.empty ? ' disabled' : ''}${day.selected ? ' selected' : ''}${day.today ? ' today' : ''}${hasSlots && !day.disabled ? ' has-slots' : ''}`}>
                    <span className="cal-day-num">{day.day || ''}</span>
                    <span
                      className={`cal-day-dot${hasSlots && !day.disabled && !day.selected ? ' active' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                )
              })}
            </div>
            <div className="cal-legend">
              <span className="cal-legend-dot" aria-hidden="true" />
              <span>Dates with a dot have interviewer slots available</span>
            </div>
            {selectedDate && (
              <div className="selected-date-badge">
                <CheckCircle size={13} />
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        {/* ── Get Slots button ───────────────────────────────────────── */}
        {selectedDate && !slotsFetched && (
          <div className="full-width" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn btn-primary get-slots-btn"
              onClick={handleGetSlots} disabled={slotsLoading}>
              {slotsLoading
                ? <><div className="spin-sm" /> Loading slots...</>
                : <><Search size={17} /> Get Interviewer Slots</>}
            </button>
          </div>
        )}

        {/* ── Interviewer slot cards ───────────────────────────────────── */}
        {(slotsFetched || slotsLoading) && (
          <div className="card full-width" ref={slotsSectionRef}>
            <div className="sch-card-header">
              <Users size={20} />
              <h3>
                {slotsFetched
                  ? `Available Interviewers — ${selectedDate!.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                  : 'Interviewers'}
              </h3>
              {slotsFetched && timezone && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Globe size={13} />
                  Times in <strong style={{ marginLeft: 3 }}>{getTzAbbr(timezone)}</strong>
                  <span style={{ color: '#9ca3af' }}>({timezone})</span>
                </span>
              )}
              {slotsLoading && <div className="spin-sm" />}
            </div>

            {slotsLoading && (
              <div className="av-banner info">
                <div className="spin-sm" />
                <div><strong>Loading slots...</strong><span>Fetching availability.</span></div>
              </div>
            )}
            {slotsFetched && totalOpenSlots === 0 && (
              <div className="av-banner warn">
                <AlertCircle size={16} />
                <div><strong>No slots available for the selected date</strong><span>Please choose another date from the calendar to see available interviewers.</span></div>
              </div>
            )}
            {slotsFetched && totalOpenSlots > 0 && (
              <div className="av-banner success">
                <CheckCircle size={16} />
                <div><strong>{totalOpenSlots} slot{totalOpenSlots !== 1 ? 's' : ''} available</strong>
                  <span>Select a time to proceed to payment.</span></div>
              </div>
            )}

            {(slotsLoading || (slotsFetched && totalOpenSlots > 0)) && <div className="iv-grid">
              {orderedProfiles.map((profile, idx) => {
                const slots = slotsById[profile.id] || []
                const isSelected = selectedInterviewerId === profile.id
                const ivTz = tzById[profile.id] ?? null
                const ivLang = langById[profile.id] ?? profile.language ?? null

                const isCurrentIv = isReschedule && profile.id === originalInterviewerId
                // Find first non-current profile index for section header logic
                const nonCurrentProfiles = orderedProfiles.filter(p => p.id !== originalInterviewerId)
                const firstRecommendedIdx = orderedProfiles.indexOf(
                  nonCurrentProfiles.find(p => matchById[p.id] === 'full' || matchById[p.id] === 'partial')!
                )
                const firstOthersIdx = orderedProfiles.indexOf(
                  nonCurrentProfiles.find(p => matchById[p.id] === 'none' || matchById[p.id] === undefined)!
                )

                const showCurrentHeader = isCurrentIv && idx === 0
                const showRecommendedHeader = slotsFetched && !isCurrentIv && idx === firstRecommendedIdx && firstRecommendedIdx !== -1
                const showOthersHeader = slotsFetched && !isCurrentIv && idx === firstOthersIdx && firstOthersIdx !== -1 && firstRecommendedIdx !== -1

                return (
                  <React.Fragment key={profile.id}>
                    {showCurrentHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot current" />
                        Your Current Interviewer
                      </div>
                    )}
                    {showRecommendedHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot recommended" />
                        Recommended for You
                      </div>
                    )}
                    {showOthersHeader && (
                      <div className="iv-section-hdr full-width">
                        <span className="iv-section-dot others" />
                        Other Interviewers
                      </div>
                    )}
                    <div className={`iv-card${isSelected ? ' iv-card-active' : ''}${isCurrentIv ? ' iv-card-current' : ''}`}>
                      <div className="iv-card-head">
                        {profile.avatar_url
                          ? <img src={profile.avatar_url} alt={profile.alias} className="iv-avatar" style={{ objectFit: 'cover' }} />
                          : <div className="iv-avatar">{profile.alias.charAt(0).toUpperCase()}</div>
                        }
                        <div style={{ flex: 1 }}>
                          <div className="iv-name">{profile.alias}</div>
                          <div className="iv-tags">
                            {ivLang && <span className="tag">{ivLang.toUpperCase()}</span>}
                            {ivTz && <span className="tag">{ivTz}</span>}
                          </div>
                        </div>
                      </div>

                      {slotsLoading ? (
                        <div className="slot-status"><div className="spin-sm" /> Loading...</div>
                      ) : !slotsFetched ? (
                        <div className="slot-status muted">Select a date and click "Get Interviewer Slots"</div>
                      ) : slots.length === 0 ? (
                        <div className="slot-status muted">No slots available on this date</div>
                      ) : (
                        <div className="slot-times">
                          {slots.map(slot => {
                            const active = isSelected && selectedSlotUtc === slot.start_utc
                            return (
                              <button key={slot.start_utc} type="button"
                                className={`slot-btn${active ? ' slot-btn-active' : ''}`}
                                onClick={() => handleSlotSelect(profile.id, slot.start_utc, slot.start)}>
                                {fmtTime(slot.start)}
                                {timezone && <span className="slot-tz">{getTzAbbr(timezone)}</span>}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )
              })}
            </div>}
          </div>
        )}

        {/* ── Book / Reschedule button ────────────────────────────────── */}
        {selectedInterviewerId && selectedSlotUtc && (
          <div className="full-width book-row">
            <div className="book-summary">
              <CheckCircle size={16} style={{ color: '#16a34a' }} />
              <span>
                <strong>{interviewerProfiles.find(p => p.id === selectedInterviewerId)?.alias}</strong>
                {' — '}
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                {' at '}
                <strong>{selectedSlotStart ? fmtTime(selectedSlotStart) : fmtTime(selectedSlotUtc!)}</strong>
                {timezone && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({getTzAbbr(timezone)})</span>}
              </span>
            </div>
            {isReschedule ? (
              <>
                <button type="button" className="btn btn-primary" style={{ padding: '14px 36px', fontSize: 16 }}
                  onClick={handleReschedule} disabled={bookingLoading}>
                  <RefreshCcw size={18} />
                  {bookingLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  No payment required. Your session will be rescheduled and both parties will be notified.
                </p>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--accent-cream, #f5f0dc)', borderRadius: 8, border: '1px solid #e0d8b0', margin: '4px 0 8px' }}>
                  <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Session fee</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--primary-dark, #1e2432)' }}>
                    {sessionPrice != null ? `$${sessionPrice.toFixed(2)}` : '—'}
                  </span>
                </div>
                <label className="tc-checkbox">
                  <input
                    type="checkbox"
                    checked={tcAccepted}
                    onChange={e => setTcAccepted(e.target.checked)}
                  />
                  <span>
                    I agree to the{' '}
                    <button
                      type="button"
                      className="tc-link"
                      onClick={(e) => { e.preventDefault(); openTcModal() }}
                    >
                      Terms &amp; Conditions
                    </button>
                    {' '}and{' '}
                    <button
                      type="button"
                      className="tc-link"
                      onClick={(e) => { e.preventDefault(); openPaymentModal() }}
                    >
                      Payment Policy
                    </button>
                    {' '}for this session.
                  </span>
                </label>
                {timezone && selectedSlotStart && (
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Globe size={13} />
                    Session time: <strong>{fmtTime(selectedSlotStart)} {getTzAbbr(timezone)}</strong> — {timezone}
                  </p>
                )}
                <button type="button" className="btn btn-primary" style={{ padding: '14px 36px', fontSize: 16 }}
                  onClick={handleBook} disabled={bookingLoading || !tcAccepted}
                  title={!tcAccepted ? 'Please accept the Terms & Conditions to continue' : undefined}>
                  <Calendar size={18} />
                  {bookingLoading
                    ? 'Redirecting to Payment...'
                    : `Book & Pay${sessionPrice != null ? ` — $${sessionPrice.toFixed(0)}` : ''}`}
                </button>
                {bookingLoading && checkoutUrl && (
                  <p style={{ margin: '4px 0 0', fontSize: 13, textAlign: 'center' }}>
                    Not redirected?{' '}
                    <a href={checkoutUrl} style={{ color: 'var(--primary-navy)', fontWeight: 600 }}>
                      Tap here to proceed to payment
                    </a>
                  </p>
                )}
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  You'll be taken to Stripe to complete payment securely.
                </p>
              </>
            )}
          </div>
        )}

      </div>

      {/* Terms & Conditions Modal */}
      <Modal
        open={showTcModal}
        onClose={() => setShowTcModal(false)}
        title={tcPolicy?.title || 'Terms & Conditions'}
        maxWidth={640}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!tcAccepted && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { setTcAccepted(true); setShowTcModal(false) }}
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                I Agree &amp; Continue
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowTcModal(false)}
              style={{ padding: '8px 18px', fontSize: 13 }}
            >
              Close
            </button>
          </div>
        }
      >
        {policiesLoading && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading policy...</p>
        )}
        {policiesError && (
          <p style={{ color: '#dc2626', fontSize: 14 }}>{policiesError}</p>
        )}
        {!policiesLoading && !policiesError && !tcPolicy && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            No policy content is currently published. Please contact support.
          </p>
        )}
        {tcPolicy && (
          <div
            className="tc-modal-body"
            style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-dark)' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(tcPolicy.body_html) }}
          />
        )}
        <style>{`
          .tc-modal-body ul, .tc-modal-body ol { padding-left: 20px; margin: 8px 0; }
          .tc-modal-body li { margin: 5px 0; }
          .tc-modal-body p { margin: 0 0 10px; }
          .tc-modal-body h3, .tc-modal-body h4 { margin: 16px 0 6px; color: var(--primary-navy); }
          .tc-modal-body strong { color: var(--primary-navy); }
          .tc-modal-body a { color: var(--primary-navy); text-decoration: underline; }
        `}</style>
      </Modal>

      {/* Payment Policy Modal */}
      <Modal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={paymentPolicy?.title || 'Payment Policy'}
        maxWidth={640}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!tcAccepted && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { setTcAccepted(true); setShowPaymentModal(false) }}
                style={{ padding: '8px 18px', fontSize: 13 }}
              >
                I Agree &amp; Continue
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowPaymentModal(false)}
              style={{ padding: '8px 18px', fontSize: 13 }}
            >
              Close
            </button>
          </div>
        }
      >
        {policiesLoading && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading policy...</p>
        )}
        {policiesError && (
          <p style={{ color: '#dc2626', fontSize: 14 }}>{policiesError}</p>
        )}
        {!policiesLoading && !policiesError && !paymentPolicy && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            No policy content is currently published. Please contact support.
          </p>
        )}
        {paymentPolicy && (
          <div
            className="tc-modal-body"
            style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-dark)' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(paymentPolicy.body_html) }}
          />
        )}
        <style>{`
          .tc-modal-body ul, .tc-modal-body ol { padding-left: 20px; margin: 8px 0; }
          .tc-modal-body li { margin: 5px 0; }
          .tc-modal-body p { margin: 0 0 10px; }
          .tc-modal-body h3, .tc-modal-body h4 { margin: 16px 0 6px; color: var(--primary-navy); }
          .tc-modal-body strong { color: var(--primary-navy); }
          .tc-modal-body a { color: var(--primary-navy); text-decoration: underline; }
          .tc-modal-body table { margin: 8px 0; }
          .tc-modal-body th, .tc-modal-body td { font-size: 13px; }
        `}</style>
      </Modal>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .sch-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  .full-width { grid-column: 1 / -1; }

  .sch-card-header {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 18px; color: var(--primary-navy);
  }
  .sch-card-header h3 { font-size: 17px; font-weight: 600; }

  .sch-alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 18px; border-radius: 10px; margin-bottom: 20px; font-size: 14px;
  }
  .sch-alert.error {
    background: rgba(239,68,68,0.08); color: #dc2626;
    border: 1px solid rgba(239,68,68,0.2);
  }

  /* Current Interviewer banner (reschedule mode) */
  .current-iv-banner {
    background: #f0f9ff; border: 1px solid #bae6fd;
    border-radius: 12px; padding: 14px 18px; margin-bottom: 20px;
  }
  .current-iv-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; color: #0369a1;
    text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px;
  }
  .current-iv-body {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  }
  .current-iv-name {
    font-size: 15px; font-weight: 600; color: var(--primary-navy);
  }
  .current-iv-hint {
    font-size: 13px; color: #0369a1; margin-left: auto;
  }

  /* Validation Modal */
  .val-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    z-index: 1000; display: flex; align-items: center; justify-content: center;
    padding: 16px;
  }
  .val-modal {
    background: white; border-radius: 16px; padding: 28px 32px;
    max-width: 460px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }
  .val-modal-header {
    display: flex; align-items: center; gap: 10px; margin-bottom: 14px; color: #dc2626;
  }
  .val-modal-header h3 { flex: 1; font-size: 18px; font-weight: 700; }
  .val-close {
    background: none; border: none; cursor: pointer; color: var(--text-muted);
    display: flex; align-items: center; padding: 4px;
  }
  .val-close:hover { color: var(--text-dark); }
  .val-modal-desc {
    font-size: 14px; color: var(--text-muted); margin-bottom: 16px;
  }
  .val-list {
    list-style: none; display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px;
  }
  .val-list li {
    display: flex; align-items: center; gap: 10px;
    font-size: 14px; font-weight: 500; color: #dc2626;
    padding: 10px 14px; background: rgba(239,68,68,0.06);
    border-radius: 8px; border-left: 3px solid #dc2626;
  }
  .val-ok-btn { width: 100%; justify-content: center; }

  /* radio */
  .radio-group { display: flex; gap: 14px; }
  .radio-option {
    flex: 1; display: flex; align-items: flex-start; gap: 10px;
    padding: 18px; border: 2px solid var(--border); border-radius: 12px;
    cursor: pointer; transition: border-color 0.2s;
  }
  .radio-option:hover { border-color: var(--primary-navy); }
  .radio-option.selected { border-color: var(--primary-navy); background: rgba(45,55,72,0.03); }
  .radio-option input { margin-top: 3px; accent-color: var(--primary-navy); }
  .radio-option strong { display: block; margin-bottom: 3px; }
  .radio-option span { font-size: 13px; color: var(--text-muted); }

  .required-notice {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 10px 14px; background: rgba(234,179,8,0.08);
    border-radius: 8px; margin-bottom: 12px; font-size: 13px; color: #854d0e;
  }

  .jd-url-hint {
    font-size: 13px; color: var(--text-muted); margin: 14px 0 0; font-weight: 500;
  }

  /* prefs + calendar side-by-side */
  .prefs-cal-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
  }

  .duration-fixed {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 14px; background: var(--accent-light,#f8f9fa);
    border-radius: 8px; font-size: 14px; font-weight: 500; color: var(--primary-navy);
  }

  /* calendar */
  .cal-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
  }
  .cal-month { font-size: 15px; font-weight: 600; }
  .cal-nav {
    width: 30px; height: 30px; border: 1px solid var(--border); background: white;
    border-radius: 7px; display: flex; align-items: center; justify-content: center;
    cursor: pointer;
  }
  .cal-nav:hover { background: var(--accent-light,#f8f9fa); }
  .cal-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 3px; }
  .cal-day-hdr { text-align: center; font-size: 11px; font-weight: 600; color: var(--text-muted); padding: 6px 0; }
  .cal-day {
    width: 100%; aspect-ratio: 1; border: none; background: none;
    border-radius: 7px; font-size: 13px; cursor: pointer; color: var(--text-dark);
    transition: background 0.15s;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  }
  .cal-day:hover:not(.disabled):not(.empty):not(.selected) { background: var(--accent-light,#f8f9fa); }
  .cal-day.empty { cursor: default; }
  .cal-day.disabled { color: #d1d5db; cursor: not-allowed; }
  .cal-day.today { font-weight: 700; border: 2px solid var(--primary-navy); }
  .cal-day.selected { background: var(--primary-navy); color: white; font-weight: 600; }
  /* Dates with available slots: bold number + a gold dot directly below it.
     The dot's space is always reserved (transparent when no slots) so every
     day number stays vertically aligned, and the dot sits right under the
     number in cells of any size (small or large/wide layouts). */
  .cal-day.has-slots { font-weight: 700; color: var(--primary-navy); }
  .cal-day.selected.has-slots { color: white; }
  .cal-day-num { line-height: 1; }
  .cal-day-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: transparent; flex-shrink: 0;
  }
  .cal-day-dot.active { background: var(--accent-gold, #d4af37); }
  .cal-legend {
    display: flex; align-items: center; gap: 7px;
    margin-top: 12px; font-size: 12px; color: var(--text-muted);
  }
  .cal-legend-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent-gold, #d4af37); flex-shrink: 0;
  }
  .selected-date-badge {
    display: flex; align-items: center; gap: 7px;
    margin-top: 12px; padding: 9px 13px;
    background: rgba(34,197,94,0.08); border-radius: 8px;
    font-size: 13px; color: #16a34a; font-weight: 500;
  }

  /* get slots button */
  .get-slots-btn { padding: 13px 36px; font-size: 15px; gap: 8px; }

  /* availability banner */
  .av-banner {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px; border-radius: 10px; margin-bottom: 18px;
  }
  .av-banner.info { background: rgba(59,130,246,0.06); color: #2563eb; }
  .av-banner.warn { background: rgba(234,179,8,0.06); color: #854d0e; }
  .av-banner.success { background: rgba(34,197,94,0.06); color: #16a34a; }
  .av-banner strong { display: block; font-size: 14px; margin-bottom: 2px; }
  .av-banner span { font-size: 13px; opacity: 0.85; }

  /* interviewer grid */
  .iv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(270px,1fr)); gap: 16px; }
  .iv-section-hdr {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px;
    color: var(--text-muted); padding: 8px 2px 4px; grid-column: 1 / -1;
  }
  .iv-section-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  }
  .iv-section-dot.recommended { background: #22c55e; }
  .iv-section-dot.others { background: #94a3b8; }
  .iv-section-dot.current { background: #0ea5e9; }
  .match-badge {
    font-size: 10px; font-weight: 700; padding: 3px 8px;
    border-radius: 10px; white-space: nowrap; flex-shrink: 0;
  }
  .match-full { background: rgba(34,197,94,0.12); color: #16a34a; }
  .match-partial { background: rgba(234,179,8,0.12); color: #92400e; }
  .match-current { background: rgba(14,165,233,0.12); color: #0369a1; }
  .iv-card {
    border: 2px solid var(--border); border-radius: 12px; padding: 18px; transition: border-color 0.2s;
  }
  .iv-card-active { border-color: var(--primary-navy); background: rgba(45,55,72,0.02); }
  .iv-card-current { border-color: #bae6fd; background: #f0f9ff; }
  .iv-card-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
  .iv-avatar {
    width: 42px; height: 42px; border-radius: 50%; background: var(--primary-navy);
    color: white; display: flex; align-items: center; justify-content: center;
    font-size: 17px; font-weight: 600; flex-shrink: 0;
  }
  .iv-name { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
  .iv-tags { display: flex; gap: 6px; flex-wrap: wrap; }
  .tag {
    font-size: 11px; color: var(--text-muted); background: var(--accent-light,#f8f9fa);
    padding: 2px 7px; border-radius: 4px;
  }
  .slot-status { font-size: 13px; color: var(--text-muted); padding: 4px 0; }
  .slot-status.muted { font-style: italic; }

  .slot-times { display: flex; gap: 8px; flex-wrap: wrap; }
  .slot-btn {
    padding: 7px 14px; border: 1px solid var(--border); background: white;
    border-radius: 8px; font-size: 13px; cursor: pointer; transition: all 0.15s;
    display: flex; flex-direction: column; align-items: center; gap: 1px;
  }
  .slot-btn:hover { border-color: var(--primary-navy); background: rgba(45,55,72,0.08); color: var(--primary-navy); }
  .slot-btn-active { background: var(--primary-navy); color: white; border-color: var(--primary-navy); }
  .slot-btn-active:hover { background: #1a2540; color: white; border-color: #1a2540; }
  .slot-tz { font-size: 10px; opacity: 0.65; line-height: 1; }

  /* booking row */
  .book-row {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 24px; background: rgba(34,197,94,0.04);
    border: 2px solid rgba(34,197,94,0.2); border-radius: 14px;
  }
  .book-summary {
    display: flex; align-items: center; gap: 8px;
    font-size: 15px; color: var(--text-dark);
  }
  .tc-checkbox {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 18px; background: rgba(255,255,255,0.7);
    border: 1px solid rgba(34,197,94,0.3); border-radius: 10px;
    cursor: pointer; font-size: 14px; color: var(--text-dark); max-width: 480px;
  }
  .tc-checkbox input[type="checkbox"] {
    margin-top: 2px; width: 17px; height: 17px; flex-shrink: 0;
    accent-color: var(--primary-navy); cursor: pointer;
  }
  .tc-checkbox strong { color: var(--primary-navy); }
  .tc-link {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: var(--primary-navy);
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
  }
  .tc-link:hover { opacity: 0.8; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* job description upload */
  .jd-upload-row {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px;
  }
  .jd-upload-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 16px; border: 2px dashed var(--border); border-radius: 8px;
    font-size: 13px; font-weight: 500; color: var(--primary-navy);
    background: rgba(45,55,72,0.03); cursor: pointer; transition: all 0.15s;
  }
  .jd-upload-btn:hover { border-color: var(--primary-navy); background: rgba(45,55,72,0.06); }
  .jd-file-pill {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 12px; border-radius: 20px; font-size: 13px; font-weight: 500;
    border: 1px solid var(--border); background: var(--accent-light,#f8f9fa);
  }
  .jd-file-pill.uploading { color: #2563eb; border-color: #93c5fd; background: rgba(59,130,246,0.06); }
  .jd-file-pill.uploaded { color: #16a34a; border-color: #86efac; background: rgba(34,197,94,0.06); }
  .jd-remove-btn {
    background: none; border: none; cursor: pointer; padding: 2px;
    color: var(--text-muted); display: flex; align-items: center;
  }
  .jd-remove-btn:hover { color: #dc2626; }
  .jd-error { font-size: 12px; color: #dc2626; }
  .jd-divider {
    display: flex; align-items: center; gap: 10px; margin: 12px 0 10px;
    font-size: 12px; color: var(--text-muted);
  }
  .jd-divider::before, .jd-divider::after {
    content: ''; flex: 1; height: 1px; background: var(--border);
  }

  /* spinner */
  .spin-sm {
    width: 15px; height: 15px; border: 2px solid rgba(0,0,0,0.15);
    border-top-color: currentColor; border-radius: 50%;
    animation: spin 0.7s linear infinite; flex-shrink: 0; display: inline-block;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @media (max-width: 768px) {
    .sch-grid { grid-template-columns: 1fr; }
    .prefs-cal-row { grid-template-columns: 1fr; }
    .radio-group { flex-direction: column; }
    .val-modal { padding: 20px; }
    .iv-grid { grid-template-columns: 1fr; }
    .book-row { padding: 16px; }
    .tc-checkbox { max-width: 100%; }
  }

  @media (max-width: 480px) {
    .sch-grid { gap: 16px; }
    .cal-grid { gap: 2px; }
    .val-modal { padding: 16px; border-radius: 12px; }
    .book-row .btn { width: 100%; justify-content: center; padding: 14px 20px; }
    .book-summary { font-size: 13px; text-align: center; justify-content: center; flex-wrap: wrap; }
  }

  @media (max-width: 360px) {
    .cal-grid { gap: 1px; }
  }
`
